/**
 * Выбор корзины ряда раскрытой строки (#2394 §9 п. 7): столбец вида «Период»
 * сужает разговоры строки до своих. Один на строку раздела (`VoiceRowDetails`)
 * и на код возражения (`ObjectionCodeCard`, #2556).
 *
 * Выбор — начало корзины, а не её номер: новое окно (период, фильтр) приносит
 * другие корзины, и старый выбор не совпадёт ни с одной, а не сузит до той,
 * что встала на его место.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { VoiceTrendBucket } from '@aica/api-types/schemas';
import { bucketIndex, bucketPeriodText, itemsInBucket } from './voice-series-view-model';
import type { VoiceRowDetailsTrend } from './voice-series-view-model';

export interface TrendBucketPick {
  /** Номер выбранной корзины; `null` — весь период. */
  index: number | null;
  bucket: VoiceTrendBucket | undefined;
  /** K строки в корзине; `null` — корзина не покрыта или не выбрана. */
  calls: number | null;
  /** Корзина словами («3 окт.», «с 30 сент.»). */
  text: string;
  select: (index: number | null) => void;
  clear: () => void;
}

export function useTrendBucket(trend: VoiceRowDetailsTrend | undefined): TrendBucketPick {
  const { t, i18n } = useTranslation('dashboard');
  const [selectedStart, setSelectedStart] = useState<string | null>(null);
  const index = trend ? bucketIndex(trend.series.buckets, selectedStart) : null;
  const bucket = trend && index !== null ? trend.series.buckets[index] : undefined;
  return {
    index,
    bucket,
    calls: bucket && index !== null ? (trend?.series.counts[index] ?? null) : null,
    text: bucket && trend ? bucketPeriodText(bucket, trend.unit, i18n.language, t) : '',
    select: (next) =>
      setSelectedStart(
        next === null ? null : (trend?.series.buckets[next]?.start ?? null)
      ),
    clear: () => setSelectedStart(null),
  };
}

/** Разговоры строки в выбранной корзине; не выбрана — все. */
export function pickedItems<T extends { createdAt: string }>(
  pick: TrendBucketPick,
  items: readonly T[]
): readonly T[] {
  return pick.bucket ? itemsInBucket(items, pick.bucket) : items;
}

/** Слова о том, каким списком сужает корзина: разговоры строки или цитаты кода. */
export interface BucketListKeys {
  notListed: string;
  shown: string;
}

/** Список строки — последние разговоры периода. */
const ROW_LIST: BucketListKeys = {
  notListed: 'voicePage.series.bucketNotListed',
  shown: 'voicePage.series.bucketShown',
};

/**
 * Строка под разговорами корзины: в ней никого, никого из списка строки, или
 * показана часть её K. `null` — сказать нечего. `keys` — чем список является:
 * у строки — последние разговоры, у кода возражения — выборка цитат.
 */
export function bucketNote(
  pick: TrendBucketPick,
  shown: number,
  t: (key: string, options?: Record<string, unknown>) => string,
  keys: BucketListKeys = ROW_LIST
): string | null {
  if (!pick.bucket) return null;
  if (pick.calls === 0) return t('voicePage.series.bucketNone', { period: pick.text });
  if (shown === 0) return t(keys.notListed, { period: pick.text });
  return pick.calls !== null && pick.calls > shown
    ? t(keys.shown, {
        shown,
        count: pick.calls,
        period: pick.text,
      })
    : null;
}
